/* eslint-disable react/no-did-update-set-state */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import classnames from 'classnames/bind'
import Icon from '../../elementals/icon'
import Text from '../../elementals/text'
import SortableList from '../sortableList'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const itemArrayPropType = PropTypes.arrayOf(
  PropTypes.shape({
    text: PropTypes.node,
    value: PropTypes.string,
  }).isRequired
)

/**
 * Default item renderer (for selected item)
 * @param {fn} onRemove - callback for when item is removed
 * @param {object} option - item data of current option
 */
const renderSelectedListItem = (onRemove, option) =>
  option && (
    <div
      key={option.value}
      data-id={option.value}
      data-elm-id='item'
      className={cx('item')}
      style={{ justifyContent: 'space-between' }}
    >
      {option.text}
      <Icon
        data-elm-id='remove_item'
        name='arrow-right'
        iconStyle='solid'
        color='grayMedium'
        hoverColor='error'
        className={cx('icon-button')}
        onClick={() => onRemove(option.value)}
      />
    </div>
  )

/**
 * Default item renderer (for unselected item)
 * @param {fn} onAdd - callback for when item is added
 * @param {object} option - item data of current option
 */
const renderUnselectedListItem = (onAdd, option) =>
  option && (
    <div
      key={option.value}
      data-id={option.value}
      data-elm-id='item'
      className={cx('item')}
    >
      <Icon
        data-elm-id='add_item'
        name='arrow-left'
        iconStyle='solid'
        style={{ marginRight: 12 }}
        color='grayMedium'
        hoverColor='success'
        className={cx('icon-button')}
        onClick={() => onAdd(option.value)}
      />
      {option.text}
    </div>
  )

/**
 * Accumulator allows user to select multiple "values" from a list of all "options"
 * "options" is an array of objects e.g. [{ value: 'BLUE', text: 'Blue' }]
 * "values" is an array of just strings  ['BLUE']
 *
 * This approach to values and options is to provide compatibility with the multi-select dropdown.
 */

class Accumulator extends Component {
  static propTypes = {
    elmId: PropTypes.string,
    allOptions: itemArrayPropType,
    initialSelectedValues: PropTypes.arrayOf(PropTypes.string),
    onUpdate: PropTypes.func.isRequired,
    selectedListHeader: PropTypes.string,
    selectedListStyle: PropTypes.object,
    selectedListItemRenderer: PropTypes.func,
    unselectedListHeader: PropTypes.string,
    unselectedListItemRenderer: PropTypes.func,
    unselectedListStyle: PropTypes.object,
    unselectedSortKey: PropTypes.object,
    helpText: PropTypes.string,
    height: PropTypes.number,
    showAsGrid: PropTypes.bool,
    autoScrollDown: PropTypes.bool,
    allowUpdate: PropTypes.func,
    hideRightPanel: PropTypes.bool,
    triggerUpdate: PropTypes.number,
  }

  static defaultProps = {
    initialSelectedValues: [],
    selectedListHeader: 'Selected',
    selectedListStyle: null,
    selectedListItemRenderer: renderSelectedListItem,
    unselectedListHeader: 'Unselected',
    unselectedListStyle: null,
    unselectedListItemRenderer: renderUnselectedListItem,
    unselectedSortKey: { key: 'text', direction: 'asc' }, // asc or desc
    helpText: '',
    height: 350,
    showAsGrid: false,
    autoScrollDown: false, // This is only for a change on initialSelectedValues
    allowUpdate: () => true,
    hideRightPanel: false,
    triggerUpdate: 0,
  }

  sortItems = items => {
    const { unselectedSortKey } = this.props
    const directionVal = unselectedSortKey.direction === 'desc' ? -1 : 1
    return items.sort((a, b) => {
      /*
        Special case for sorting Date obj,
        1. a.date === b.date is always false, it causes confusing if we have 2 obj which has same updateOn key;
        2. To avoid that, we manually sort based on photo value if their updatedDate is same
      */
      if (
        !!unselectedSortKey.key &&
        a[unselectedSortKey.key] instanceof Date &&
        b[unselectedSortKey.key] instanceof Date &&
        a[unselectedSortKey.key].getTime() ===
          b[unselectedSortKey.key].getTime()
      ) {
        return a?.value > b?.value ? directionVal : 0 - directionVal
      }
      return a[unselectedSortKey.key] > b[unselectedSortKey.key]
        ? directionVal
        : 0 - directionVal
    })
  }

  /**
   * We are assuming that the unselected list will always be auto-sorted (not manually sorted)
   * @param {Array} selectedOptions
   */
  getUnselectedOptions = selectedOptions =>
    this.sortItems(
      _.differenceBy(this.props.allOptions, selectedOptions, 'value')
    )

  constructor(props) {
    super(props)
    const { initialSelectedValues, allOptions } = props
    const selectedOptions = initialSelectedValues.map(value =>
      _.find(allOptions, { value })
    )

    this.state = {
      allOptions,
      selectedOptions,
      unselectedOptions: this.getUnselectedOptions(selectedOptions),
      filterText: '',
    }
  }

  refreshElement = el => {
    if (!el) return
    const display = window.getComputedStyle(el)?.display
    el.style.display = 'none'
    el.offsetHeight // eslint-disable-line no-unused-expressions
    el.style.display = display
  }

  componentDidMount() {
    /**
     * This is a hack for Firefox/Windows where it fails to render the list
     * in an accumulator sitting on top of a grid.  Exact reason for this issue
     * is not fully known - but refreshing the display seems to fix it.
     */
    this.refreshElement(this.selectedListRef?.el)
    this.refreshElement(this.unselectedListRef?.el)
  }

  componentDidUpdate(prevProps, prevState) {
    const { allOptions, initialSelectedValues, autoScrollDown, triggerUpdate } =
      this.props

    if (triggerUpdate !== prevProps.triggerUpdate) {
      const selectedOptions = initialSelectedValues.map(value =>
        _.find(allOptions, { value })
      )
      const unselectedOptions = this.getUnselectedOptions(selectedOptions)
      setTimeout(() => {
        this.setState({
          unselectedOptions,
        })
      }, 10)
    }

    if (!_.isEqual(prevProps.initialSelectedValues, initialSelectedValues)) {
      const selectedOptions = initialSelectedValues.map(value =>
        _.find(allOptions, { value })
      )
      this.setState(
        {
          selectedOptions,
          allOptions,
        },
        () => {
          if (autoScrollDown && this.selectedListRef)
            _.delay(() => {
              this.selectedListRef.el.scrollTop =
                this.selectedListRef.el.scrollHeight
            })
        }
      )
    }
  }

  arraysMatch = (a, b) => a.length === b.length && a.join('|') === b.join('|')

  getValuesFromOptions = options => options.map(option => option.value)

  onChange = (options, isSelectedList) => {
    const { allowUpdate } = this.props
    if (!allowUpdate || !allowUpdate(options, isSelectedList)) return // don't change a thing for me ♬

    if (isSelectedList) {
      const curValues = this.getValuesFromOptions(this.state.selectedOptions)
      const newValues = this.getValuesFromOptions(options)

      if (!this.arraysMatch(curValues, newValues)) {
        this.setState({
          selectedOptions: options,
        })
        this.props.onUpdate(newValues) // to outside, we pass just array of values
      }
    } else {
      this.setState({
        unselectedOptions: this.sortItems(options),
      })
    }
  }

  onAdd = value => {
    const newlySelectedOptions = [
      ...this.state.selectedOptions,
      _.find(this.state.allOptions, { value }),
    ]

    this.setState({
      selectedOptions: newlySelectedOptions,
      unselectedOptions: this.getUnselectedOptions(newlySelectedOptions),
    })

    this.props.onUpdate(newlySelectedOptions.map(option => option.value)) // to outside, we pass just values

    // autoscroll selected list when adding using add button (which adds to end always)
    if (this.selectedListRef)
      _.delay(() => {
        this.selectedListRef.el.scrollTop = this.selectedListRef.el.scrollHeight
      })
  }

  onRemove = value => {
    const removeOption = _.find(this.state.selectedOptions, { value })
    if (removeOption && removeOption.isNewUploaded) {
      removeOption.openDeleteModal(removeOption)
      return
    }
    const newlySelectedOptions = _.filter(
      this.state.selectedOptions,
      option => option.value !== value
    )
    this.setState({
      selectedOptions: newlySelectedOptions,
      unselectedOptions: this.getUnselectedOptions(newlySelectedOptions),
    })

    this.props.onUpdate(newlySelectedOptions.map(option => option.value)) // to outside, we pass just values
  }

  render() {
    const {
      elmId,
      height,
      selectedListHeader,
      selectedListStyle,
      selectedListItemRenderer,
      unselectedListHeader,
      unselectedListStyle,
      unselectedListItemRenderer,
      helpText,
      showAsGrid,
      hideRightPanel,
    } = this.props
    return (
      <div data-elm-id={elmId} style={{ height }} className={cx('container')}>
        <div className={cx('panel-container')}>
          <div className={cx('panel', 'left-side')} style={selectedListStyle}>
            <Text data-elm-id='selected_list_header' size='tiny' weight='heavy'>
              {selectedListHeader}
            </Text>
            <SortableList
              elmId='selected_list'
              captureListRef={ref => {
                if (ref) this.selectedListRef = ref.sortable
              }}
              list={this.state.selectedOptions || []}
              canSort
              onChange={values => {
                this.onChange(values, true)
              }}
              optionRenderer={selectedListItemRenderer.bind(
                null,
                this.onRemove
              )}
              showAsGrid={showAsGrid}
            />
          </div>
          {!hideRightPanel && (
            <div
              className={cx('panel', 'right-side', { 'grid-mode': showAsGrid })}
              style={unselectedListStyle}
            >
              <div className={cx('panel-header')}>
                <Text
                  data-elm-id='unselected_list_header'
                  size='tiny'
                  weight='heavy'
                >
                  {unselectedListHeader}
                </Text>
                <input
                  data-elm-id='unselected_filter'
                  className={cx('filter-input')}
                  type='text'
                  placeholder='Filter...'
                  value={this.state.filterText}
                  onChange={e => this.setState({ filterText: e.target.value })}
                />
              </div>
              <SortableList
                elmId='unselected_list'
                captureListRef={ref => {
                  if (ref) this.unselectedListRef = ref.sortable
                }}
                list={
                  this.state.filterText
                    ? (this.state.unselectedOptions || []).filter(option => {
                        const text =
                          typeof option.text === 'string'
                            ? option.text
                            : option.value
                        return text
                          .toLowerCase()
                          .includes(this.state.filterText.toLowerCase())
                      })
                    : this.state.unselectedOptions || []
                }
                canSort
                onChange={values => {
                  this.onChange(values, false)
                }}
                optionRenderer={unselectedListItemRenderer.bind(
                  null,
                  this.onAdd
                )}
                showAsGrid={showAsGrid}
              />
            </div>
          )}
        </div>
        {helpText && (
          <p data-elm-id='help_text' className={cx('help-text')}>
            {helpText}
          </p>
        )}
      </div>
    )
  }
}

export default Accumulator
